/**
 * PURPOSE: Revisión de vínculos SISDEU ↔ institución de Winscore (FTR-0017 Phase 4, D2).
 * RESPONSIBILITIES: Listar los vínculos aprobados de la compañía; consultar candidatos SISDEU en vivo
 *   (niveles alta/media/sin coincidencia) solo cuando el administrador abre esa pestaña; aprobar en lote
 *   los de nivel ALTA (id y nombre coinciden, institución activa); revisar uno por uno los de nivel MEDIA
 *   (nunca aprueba solo por id o solo por nombre); desvincular con confirmación explícita de que se
 *   revocarán las sesiones SISDEU afectadas; sincronizar cuentas de un vínculo aprobado y mostrar el
 *   resultado por ítem. Nunca aprueba nada sin una acción explícita del administrador.
 * COLLABORATORS: sisdeuAdminService.js, sisdeuAdmin.utils.js, CompanyInstitutionsTab.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/SisdeuInstitutionLinksModal.jsx
 */
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  approveInstitutionLinks,
  getSisdeuCandidates,
  listInstitutionLinks,
  syncInstitutionSisdeuAccounts,
  unlinkInstitutionSisdeu,
} from "../../services/sisdeuAdminService.js";
import {
  formatSyncSummary,
  getConfidenceTierLabel,
  getSisdeuAdminErrorMessage,
  isCompanyAdminForbiddenError,
  isDelegatePortalUnavailableError,
  isSisdeuCompanyDisabledError,
} from "./sisdeuAdmin.utils.js";

const SUBTABS = [
  { id: "links", label: "Vínculos aprobados" },
  { id: "candidates", label: "Candidatos SISDEU" },
];

const SECONDARY_BUTTON_CLASS =
  "rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50";

const classifyError = (error) => {
  if (isSisdeuCompanyDisabledError(error)) return { tone: "disabled", message: getSisdeuAdminErrorMessage(error, "") };
  if (isCompanyAdminForbiddenError(error) || isDelegatePortalUnavailableError(error)) {
    return { tone: "forbidden", message: getSisdeuAdminErrorMessage(error, "No tienes acceso a esta sección.") };
  }
  return { tone: "error", message: getSisdeuAdminErrorMessage(error, "Ocurrió un error inesperado.") };
};

const Notice = ({ notice }) => (
  <div
    className={`rounded-lg border p-4 text-sm ${
      notice.tone === "disabled" ? "border-slate-200 bg-slate-50 text-slate-600" : "border-amber-200 bg-amber-50 text-amber-800"
    }`}
  >
    {notice.message}
  </div>
);

const SisdeuInstitutionLinksModal = ({ onClose }) => {
  const [subtab, setSubtab] = useState("links");

  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [linksNotice, setLinksNotice] = useState(null);
  const [busyInstitutionId, setBusyInstitutionId] = useState(null);
  const [syncResult, setSyncResult] = useState(null); // { institutionName, summary }

  const [candidates, setCandidates] = useState(null);
  // Distinto de `candidates === null`: sin esta bandera, un rechazo (candidatos sigue en null) reintenta
  // en bucle infinito cada vez que `loadingCandidates` vuelve a `false` en el mismo efecto.
  const [candidatesRequested, setCandidatesRequested] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidatesNotice, setCandidatesNotice] = useState(null);
  const [mediumChoice, setMediumChoice] = useState({}); // institutionId -> externalInstitutionId
  const [approving, setApproving] = useState(false);

  const loadLinks = useCallback(async () => {
    setLoadingLinks(true);
    setLinksNotice(null);
    try {
      setLinks(await listInstitutionLinks());
    } catch (error) {
      setLinksNotice(classifyError(error));
    } finally {
      setLoadingLinks(false);
    }
  }, []);

  const loadCandidates = useCallback(async () => {
    setCandidatesRequested(true);
    setLoadingCandidates(true);
    setCandidatesNotice(null);
    try {
      setCandidates(await getSisdeuCandidates());
    } catch (error) {
      setCandidatesNotice(classifyError(error));
    } finally {
      setLoadingCandidates(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  useEffect(() => {
    if (subtab === "candidates" && !candidatesRequested) loadCandidates();
  }, [subtab, candidatesRequested, loadCandidates]);

  const refreshAfterMutation = async () => {
    await loadLinks();
    // Fuerza una relectura en vivo la próxima vez que se abra "Candidatos" (aprobar cambia el catálogo).
    setCandidates(null);
    setCandidatesRequested(false);
  };

  // El backend ya entrega un mensaje humano por cada rechazo (institution-links.service.js); no hace
  // falta re-derivarlo desde el código.
  const reportApprovalResult = ({ approved, rejected }) => {
    if (approved.length) toast.success(`${approved.length} vínculo(s) aprobado(s).`);
    rejected.forEach((item) => {
      toast.error(`Institución ${item.institutionId}: ${item.message || "no se pudo vincular."}`);
    });
  };

  const handleApproveHigh = async () => {
    if (!candidates?.high?.length) return;
    const payloadLinks = candidates.high.map((item) => ({
      institutionId: item.institution.id,
      externalInstitutionId: item.external.externalInstitutionId,
    }));
    if (
      !window.confirm(
        `¿Aprobar en lote ${payloadLinks.length} vínculo(s) de nivel ALTA (mismo id y mismo nombre normalizado, institución SISDEU activa)?`
      )
    ) {
      return;
    }
    setApproving(true);
    try {
      const result = await approveInstitutionLinks(payloadLinks);
      reportApprovalResult(result);
      await refreshAfterMutation();
    } catch (error) {
      toast.error(getSisdeuAdminErrorMessage(error, "No se pudo aprobar el lote."));
    } finally {
      setApproving(false);
    }
  };

  const handleApproveMedium = async (institutionId) => {
    const externalInstitutionId = mediumChoice[institutionId];
    if (!externalInstitutionId) {
      toast.error("Elige con qué institución SISDEU corresponde antes de aprobar.");
      return;
    }
    if (!window.confirm("¿Aprobar este vínculo? Un id o un nombre coincidentes por sí solos no bastan: confirmas que corresponden a la misma institución.")) {
      return;
    }
    setApproving(true);
    try {
      const result = await approveInstitutionLinks([{ institutionId, externalInstitutionId: Number(externalInstitutionId) }]);
      reportApprovalResult(result);
      await refreshAfterMutation();
    } catch (error) {
      toast.error(getSisdeuAdminErrorMessage(error, "No se pudo aprobar el vínculo."));
    } finally {
      setApproving(false);
    }
  };

  const handleUnlink = async (link) => {
    if (
      !window.confirm(
        `¿Desvincular "${link.institution?.name}" de SISDEU? Se revocarán las sesiones SISDEU activas de sus cuentas. Los permisos ya otorgados no se transfieren a nadie.`
      )
    ) {
      return;
    }
    setBusyInstitutionId(link.institution?.id);
    try {
      const updated = await unlinkInstitutionSisdeu(link.institution.id);
      toast.success(`Vínculo retirado. Sesiones revocadas: ${updated.sessionsRevoked ?? 0}.`);
      await loadLinks();
    } catch (error) {
      toast.error(getSisdeuAdminErrorMessage(error, "No se pudo desvincular la institución."));
    } finally {
      setBusyInstitutionId(null);
    }
  };

  const handleSync = async (link) => {
    setBusyInstitutionId(link.institution?.id);
    setSyncResult(null);
    try {
      const { summary } = await syncInstitutionSisdeuAccounts(link.institution.id);
      setSyncResult({ institutionName: link.institution?.name, summary });
      await loadLinks();
    } catch (error) {
      toast.error(getSisdeuAdminErrorMessage(error, "No se pudo sincronizar las cuentas."));
    } finally {
      setBusyInstitutionId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sisdeu-links-title"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 id="sisdeu-links-title" className="text-xl font-bold text-slate-800">
              Vínculos SISDEU de institución
            </h3>
            <p className="text-sm text-slate-500">
              Un id o un nombre coincidentes por sí solos nunca bastan para vincular automáticamente.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-3 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="mb-4 flex gap-2 border-b border-slate-200" role="tablist">
          {SUBTABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={subtab === tab.id}
              onClick={() => setSubtab(tab.id)}
              className={`px-3 py-2 text-sm font-semibold ${
                subtab === tab.id ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {subtab === "links" ? (
          loadingLinks ? (
            <div className="py-10 text-center text-sm text-slate-500">Cargando...</div>
          ) : linksNotice ? (
            <Notice notice={linksNotice} />
          ) : (
            <div className="space-y-3">
              {syncResult && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  Sincronización de <strong>{syncResult.institutionName}</strong>: {formatSyncSummary(syncResult.summary)}
                </div>
              )}
              {links.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
                  Esta compañía aún no tiene vínculos SISDEU aprobados.
                </div>
              ) : (
                links.map((link) => (
                  <article key={link.id} className="rounded-lg border border-slate-200 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-slate-800">
                          {link.institution?.name}
                          {link.institution?.abrev ? ` · ${link.institution.abrev}` : ""}
                        </p>
                        <p className="text-xs text-slate-500">
                          Id externo SISDEU: {link.externalInstitutionId} · Identidades: {link.identities?.total ?? 0}
                          {link.identities?.blocked ? ` (${link.identities.blocked} bloqueada(s))` : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() => handleSync(link)}
                          disabled={busyInstitutionId === link.institution?.id}
                          className={SECONDARY_BUTTON_CLASS}
                        >
                          {busyInstitutionId === link.institution?.id ? "Sincronizando..." : "Sincronizar cuentas"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUnlink(link)}
                          disabled={busyInstitutionId === link.institution?.id}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          Desvincular
                        </button>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          )
        ) : loadingCandidates ? (
          <div className="py-10 text-center text-sm text-slate-500">Consultando SISDEU en vivo...</div>
        ) : candidatesNotice ? (
          <div className="space-y-3">
            <Notice notice={candidatesNotice} />
            {candidatesNotice.tone !== "disabled" && (
              <button type="button" onClick={() => setCandidatesRequested(false)} className={SECONDARY_BUTTON_CLASS}>
                Reintentar
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="font-semibold text-slate-800">
                  Nivel {getConfidenceTierLabel("high")} ({candidates?.high?.length ?? 0})
                </h4>
                {Boolean(candidates?.high?.length) && (
                  <button type="button" onClick={handleApproveHigh} disabled={approving} className={SECONDARY_BUTTON_CLASS}>
                    {approving ? "Aprobando..." : "Aprobar todos"}
                  </button>
                )}
              </div>
              {!candidates?.high?.length ? (
                <p className="text-sm text-slate-500">Sin candidatos de coincidencia alta.</p>
              ) : (
                <ul className="space-y-1 text-sm text-slate-700">
                  {candidates.high.map((item) => (
                    <li key={item.institution.id} className="rounded border border-emerald-100 bg-emerald-50 px-3 py-2">
                      {item.institution.name} ↔ {item.external.name} (id externo {item.external.externalInstitutionId})
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h4 className="mb-2 font-semibold text-slate-800">
                Nivel {getConfidenceTierLabel("medium")} ({candidates?.medium?.length ?? 0})
              </h4>
              {!candidates?.medium?.length ? (
                <p className="text-sm text-slate-500">Sin candidatos de coincidencia media.</p>
              ) : (
                <ul className="space-y-2">
                  {candidates.medium.map((item) => (
                    <li key={item.institution.id} className="rounded border border-amber-200 bg-amber-50 p-3">
                      <p className="text-sm font-semibold text-slate-800">{item.institution.name}</p>
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
                        <label className="block flex-1 text-xs font-medium text-slate-700">
                          Institución SISDEU correspondiente
                          <select
                            value={mediumChoice[item.institution.id] || ""}
                            onChange={(event) =>
                              setMediumChoice((current) => ({ ...current, [item.institution.id]: event.target.value }))
                            }
                            className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                          >
                            <option value="">Selecciona...</option>
                            {item.candidates.map((candidate) => (
                              <option key={candidate.external.externalInstitutionId} value={candidate.external.externalInstitutionId}>
                                {candidate.external.name} (id {candidate.external.externalInstitutionId}, señal:{" "}
                                {candidate.signals.join(", ")})
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          onClick={() => handleApproveMedium(item.institution.id)}
                          disabled={approving}
                          className={SECONDARY_BUTTON_CLASS}
                        >
                          Aprobar
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h4 className="mb-2 font-semibold text-slate-800">
                Sin coincidencia ({candidates?.unmatched?.length ?? 0})
              </h4>
              <p className="text-xs text-slate-500">
                Ninguna señal de id o nombre. Requieren un id externo confirmado directamente por FEDUP antes de poder
                vincularse.
              </p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default SisdeuInstitutionLinksModal;
